/**
 * 全局音乐律动：按 deviceId 将常规灯效快照存入 IndexedDB（主应用 / 键盘子应用同源共享）。
 */

import { isHfdNonRestorableLightingMode } from "./routineLightingDefaults";

export type HfdRoutineEffectSnapshot = {
  mode: number;
  red: number;
  green: number;
  blue: number;
  colorMode: number;
  brightness: number;
  speed: number;
  direction: 0 | 1;
  effectModeType: 0 | 1 | 2 | 3;
};

export type RoutineLightingIdbRecord = {
  deviceId: string;
  routineBase: Record<string, unknown>;
  customLighting?: unknown[] | null;
  /** 华奋达 getLEDEffect / setLEDEffect */
  hfdRoutineEffect?: HfdRoutineEffectSnapshot | null;
  equipment?: Record<string, unknown>;
  updatedAt: number;
};

const DB_NAME = "eweadn-qiankun-music-rhythm";
const DB_VERSION = 1;
const STORE_NAME = "deviceRoutineSnapshots";

let dbPromise: Promise<IDBDatabase> | null = null;

function openRoutineLightingDb(): Promise<IDBDatabase> {
  if (typeof indexedDB === "undefined") {
    return Promise.reject(new Error("indexedDB unavailable"));
  }
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onerror = () =>
        reject(req.error ?? new Error("indexedDB open failed"));
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      };
      req.onsuccess = () => resolve(req.result);
    });
  }
  return dbPromise;
}

export function isValidRoutineLightingSnapshotData(
  data: Record<string, unknown> | null | undefined
): boolean {
  if (!data || typeof data !== "object") return false;
  const idField = Number(data.id);
  if (Number.isFinite(idField) && idField !== 20) return true;
  const mode = Number(data.mode);
  return !Number.isNaN(mode) && mode !== 20;
}

function normalizeHfdEffect(raw: unknown): HfdRoutineEffectSnapshot | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const mode = Number(o.mode);
  if (Number.isNaN(mode) || isHfdNonRestorableLightingMode(mode)) return null;
  return {
    mode,
    red: Number(o.red) || 0,
    green: Number(o.green) || 0,
    blue: Number(o.blue) || 0,
    colorMode: Number(o.colorMode) ?? 1,
    brightness: Number(o.brightness) ?? 5,
    speed: Number(o.speed) ?? 0,
    direction: (Number(o.direction) === 1 ? 1 : 0) as 0 | 1,
    effectModeType: (Number(o.effectModeType) as 0 | 1 | 2 | 3) ?? 0,
  };
}

/** 写入 / 覆盖某台设备的完整灯光快照对象 */
export async function saveRoutineLightingSnapshotToIdb(
  deviceId: string,
  routineBase: Record<string, unknown>,
  options?: {
    equipment?: Record<string, unknown> | null;
    customLighting?: unknown[] | null;
    hfdRoutineEffect?: HfdRoutineEffectSnapshot | null;
  }
): Promise<void> {
  if (!deviceId || !isValidRoutineLightingSnapshotData(routineBase)) {
    return;
  }
  const record: RoutineLightingIdbRecord = {
    deviceId,
    routineBase: { ...routineBase },
    equipment: options?.equipment ? { ...options.equipment } : undefined,
    customLighting: Array.isArray(options?.customLighting)
      ? options.customLighting
      : undefined,
    hfdRoutineEffect: options?.hfdRoutineEffect ?? undefined,
    updatedAt: Date.now(),
  };
  const db = await openRoutineLightingDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const req = store.put(record, deviceId);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error ?? new Error("idb put failed"));
  });
}

/** 按 deviceId 读取完整快照记录 */
export async function loadDeviceLightingSnapshotRecord(
  deviceId: string
): Promise<RoutineLightingIdbRecord | null> {
  if (!deviceId) return null;
  try {
    const db = await openRoutineLightingDb();
    const record = await new Promise<RoutineLightingIdbRecord | undefined>(
      (resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readonly");
        const req = tx.objectStore(STORE_NAME).get(deviceId);
        req.onsuccess = () =>
          resolve(req.result as RoutineLightingIdbRecord | undefined);
        req.onerror = () => reject(req.error ?? new Error("idb get failed"));
      }
    );
    if (!record?.routineBase) return null;
    return isValidRoutineLightingSnapshotData(record.routineBase)
      ? record
      : null;
  } catch (e) {
    console.warn("[RoutineLightingIdb] load record failed:", deviceId, e);
    return null;
  }
}

/** 按 deviceId 读取常规灯效 data（即 record.routineBase） */
export async function loadRoutineLightingSnapshotFromIdb(
  deviceId: string
): Promise<Record<string, unknown> | null> {
  const record = await loadDeviceLightingSnapshotRecord(deviceId);
  return record?.routineBase ?? null;
}

export async function loadRoutineLightingCustomFromIdb(
  deviceId: string
): Promise<unknown[] | null> {
  const record = await loadDeviceLightingSnapshotRecord(deviceId);
  return Array.isArray(record?.customLighting) ? record.customLighting : null;
}

export async function loadHfdRoutineEffectFromIdb(
  deviceId: string
): Promise<HfdRoutineEffectSnapshot | null> {
  const record = await loadDeviceLightingSnapshotRecord(deviceId);
  const hfd = record?.hfdRoutineEffect;
  if (!hfd || Number(hfd.mode) === 20) return null;
  return hfd;
}

/** 枚举 IndexedDB 中已有快照的 deviceId */
export async function listAllDeviceLightingSnapshotIds(): Promise<string[]> {
  try {
    const db = await openRoutineLightingDb();
    return await new Promise<string[]>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const req = tx.objectStore(STORE_NAME).getAllKeys();
      req.onsuccess = () => {
        const keys = req.result ?? [];
        resolve(keys.map((k) => String(k)));
      };
      req.onerror = () => reject(req.error);
    });
  } catch {
    return [];
  }
}

export async function deleteRoutineLightingSnapshotFromIdb(
  deviceId: string
): Promise<void> {
  if (!deviceId) return;
  try {
    const db = await openRoutineLightingDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const req = tx.objectStore(STORE_NAME).delete(deviceId);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  } catch {
    // ignore
  }
}

export { normalizeHfdEffect };
